/**
 * CaseManagerSection
 *
 * Optional section for selecting a case manager during matter creation/update.
 * Only shows if the selected firm has case managers configured.
 * Includes auto_task_assignment toggle when a CM is selected.
 */
import React, { useEffect } from 'react';
import {
  Control,
  Controller,
  UseFormSetValue,
  useWatch,
} from 'react-hook-form';

import { Checkbox, CheckboxField } from '@/components/ui/catalyst/checkbox';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useGetFirmCaseManagersQuery } from '@/services/api/caseManagerService';

import { FormSection } from './FormSection';
import { CreateMatterFormValues } from './types';

interface CaseManagerSectionProps {
  control: Control<CreateMatterFormValues>;
  /** The selected firm ID - required to fetch case managers */
  firmId: string | null | undefined;
  /** Whether the form is in loading state */
  isLoading: boolean;
  /** Form setValue function for updating auto_task_assignment */
  setValue: UseFormSetValue<CreateMatterFormValues>;
}

export const CaseManagerSection: React.FC<CaseManagerSectionProps> = ({
  control,
  firmId,
  isLoading,
  setValue,
}) => {
  // Fetch case managers for the selected firm
  const { data: caseManagers = [], isLoading: isLoadingCaseManagers } =
    useGetFirmCaseManagersQuery({ firmId: firmId || '' }, { skip: !firmId });

  // Watch the selected case manager to show/hide toggle
  const selectedCaseManagerId = useWatch({
    control,
    name: 'case_manager_id',
  });

  const hasCaseManagerSelected =
    selectedCaseManagerId && selectedCaseManagerId !== 'none';

  // When case manager selection changes, default auto_task_assignment based on CM's case_auto_assign
  useEffect(() => {
    if (hasCaseManagerSelected) {
      const selectedCm = caseManagers.find(
        (cm) => cm.id === selectedCaseManagerId,
      );
      // Default to the CM's case_auto_assign setting, or true if not set
      const defaultValue = selectedCm?.case_auto_assign ?? true;
      setValue('auto_task_assignment', defaultValue);
    } else {
      // Clear when no CM selected
      setValue('auto_task_assignment', undefined);
    }
  }, [selectedCaseManagerId, caseManagers, setValue, hasCaseManagerSelected]);

  // Don't render if no firm selected or firm has no case managers
  if (!firmId || caseManagers.length === 0) {
    return null;
  }

  return (
    <FormSection title="Case Manager" description="Optional">
      <div className="space-y-3">
        <Controller
          name="case_manager_id"
          control={control}
          render={({ field }) => (
            <Select
              value={field.value || 'none'}
              onValueChange={(value) =>
                field.onChange(value === 'none' ? null : value)
              }
              disabled={isLoading || isLoadingCaseManagers}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="None (optional)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">
                  <span className="text-zinc-500 dark:text-zinc-400">None</span>
                </SelectItem>
                {caseManagers.map((cm) => (
                  <SelectItem key={cm.id} value={cm.id}>
                    {cm.display_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        />

        {/* Auto-assign toggle - only show when CM is selected */}
        {hasCaseManagerSelected && (
          <Controller
            name="auto_task_assignment"
            control={control}
            render={({ field }) => (
              <CheckboxField>
                <Checkbox
                  checked={field.value ?? true}
                  onChange={(checked) => field.onChange(Boolean(checked))}
                  disabled={isLoading}
                  color="indigo"
                />
                <Label className="text-sm cursor-pointer">
                  Auto-assign tasks
                </Label>
              </CheckboxField>
            )}
          />
        )}
        {hasCaseManagerSelected && (
          <p className="text-xs text-zinc-500 dark:text-zinc-400 ml-7 -mt-1">
            Existing incomplete tasks and new tasks go to the case manager
          </p>
        )}
      </div>
    </FormSection>
  );
};
